import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../../services/api';

const SUITE_OPTIONS = ['Strategic Series', 'FirstChoice Series', 'Private Market'];

const SRM_BAND_LABELS = {
  1: 'Very Low', 2: 'Low', 3: 'Low to Medium',
  4: 'Medium', 5: 'Medium to High', 6: 'High', 7: 'Very High',
};

// Known suites keep their brand colours; every other suite is assigned a colour
// by its position in the sorted list of distinct suites (see suiteColourMap).
// This guarantees each distinct suite is unique until the palette wraps.
const SUITE_COLOURS = {
  'Strategic Series': '#072040',
  'FirstChoice Series': '#599BE3',
  'Private Market': '#f0a500',
};

// Distinct, mid-to-dark palette — all readable with white text. Excludes the
// three brand colours above so non-brand suites never clash with them.
const SUITE_PALETTE = [
  '#2E7D32', '#C62828', '#6A1B9A', '#00838F', '#AD1457', '#4E342E',
  '#1565C0', '#EF6C00', '#283593', '#00695C', '#9E9D24', '#5D4037',
  '#7B1FA2', '#0277BD', '#D84315', '#455A64', '#558B2F', '#B71C1C',
  '#4527A0', '#00796B', '#8D6E63',
];

// Apps the portfolio registry can scope visibility to. Order = display order in the form.
const APP_OPTIONS = [
  { key: 'portfolio_lab', label: 'Portfolio Lab' },
  { key: 'risk_model', label: 'Risk Model' },
  { key: 'retirement_calculator', label: 'Retirement Calculator' },
  { key: 'allocation_calculator', label: 'Allocation Calculator' },
  { key: 'portfolio_viewer', label: 'Portfolio Viewer / Dashboard / Comparison / Blender' },
  { key: 'factsheets', label: 'Factsheets' },
  { key: 'portfolio_reports', label: 'Portfolio Reports' },
  { key: 'portfolio_attribution', label: 'Portfolio Attribution' },
];
const ALL_APP_KEYS = APP_OPTIONS.map((a) => a.key);

const emptyForm = {
  portfolio_code: '',
  blender_code: '',
  simulation_name: '',
  default_display_name: '',
  suite: '',
  growth_exposure: '',
  growth_range: '',
  income_yield: null,        // display only: calculated bottom-up by the server
  income_yield_error: null,
  return_objective: '',
  objective_type: 'cpi',
  time_horizon: '',
  display_order: 0,
  is_active: true,
  tailored: false,  // true = hidden from branded clients unless granted to a domain
  show_performance: true,  // false = hide historic performance in client apps
  visible_in_apps: null,  // null = visible in all apps
  reporting_frequency: 'quarterly',
  srm_override: '',
  benchmark_override: '',
  inception_date: '',
  performance_source: '',
};

const PortfolioRegistryEditor = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingCode, setEditingCode] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [customSuite, setCustomSuite] = useState('');
  const [useCustomSuite, setUseCustomSuite] = useState(false);
  const [benchmarkOptions, setBenchmarkOptions] = useState([]);
  const [copyFromCode, setCopyFromCode] = useState('');

  // Taxonomy (for grouping the read-only SAA preview) + the master SAAs built in
  // the Historic SAA tab. A portfolio no longer carries its own editable weights —
  // it inherits the current (latest-FY) SAA of the master it points to (blender_code).
  const [taxonomy, setTaxonomy] = useState(null);
  const [masterSAAs, setMasterSAAs] = useState({}); // blender_code -> {name, latest_fy, weights(decimals)}
  const [sacWeights, setSacWeights] = useState({}); // inherited weights for the portfolio being edited (decimals)
  const [showWeights, setShowWeights] = useState(false);
  const [useCustomBlender, setUseCustomBlender] = useState(false);

  const fetchPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getDefaultPortfolios();
      setPortfolios(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, []);

  // Build the master-SAA map from the Historic SAA registry: each blender_code's
  // latest financial year is its "current" SAA. Weights arrive as percentages.
  const fetchMasterSAAs = useCallback(async () => {
    try {
      const data = await adminAPI.getHistoricSAA();
      const nameByBc = {};
      (data.portfolios || []).forEach((p) => { nameByBc[p.blender_code] = p.name; });
      const latestFy = {};
      (data.rows || []).forEach((r) => {
        if (latestFy[r.blender_code] == null || r.financial_year > latestFy[r.blender_code]) {
          latestFy[r.blender_code] = r.financial_year;
        }
      });
      const map = {};
      (data.rows || []).forEach((r) => {
        if (r.financial_year !== latestFy[r.blender_code]) return;
        if (!map[r.blender_code]) {
          map[r.blender_code] = { name: nameByBc[r.blender_code] || r.blender_code, latest_fy: latestFy[r.blender_code], weights: {} };
        }
        map[r.blender_code].weights[r.sub_asset_class] = (r.weight || 0) / 100; // -> decimal
      });
      setMasterSAAs(map);
    } catch {
      // Non-fatal: the preview just won't populate.
      setMasterSAAs({});
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
    adminAPI.getBenchmarkComparitors().then(setBenchmarkOptions).catch(() => {});
    adminAPI.getTaxonomy().then(setTaxonomy).catch(() => {});
    fetchMasterSAAs();
  }, [fetchPortfolios, fetchMasterSAAs]);

  // Keep the read-only preview in step with the selected master SAA (blender_code).
  useEffect(() => {
    const m = masterSAAs[form.blender_code];
    setSacWeights(m ? m.weights : {});
  }, [form.blender_code, masterSAAs]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // On a new portfolio, the Master SAA (blender code) mirrors the Portfolio Code
  // as it's typed — until the user edits the blender field themselves, at which
  // point it stops tracking (detected by blender_code diverging from the code).
  const handlePortfolioCodeChange = (value) => {
    setForm((prev) => {
      const wasTracking = !editingCode && prev.blender_code === prev.portfolio_code;
      if (wasTracking) {
        // Keep the blender-code entry mode in step: known master SAA → dropdown,
        // otherwise free-text so an arbitrary code can still be entered.
        setUseCustomBlender(!!value && !masterSAAs[value]);
        return { ...prev, portfolio_code: value, blender_code: value };
      }
      return { ...prev, portfolio_code: value };
    });
  };

  // Toggle a single app key. null state means "visible everywhere"; we expand
  // it to an explicit list as soon as the user unticks one. If the user
  // re-ticks every app, collapse back to null so the row reads as "all apps".
  const toggleAppVisibility = (key, checked) => {
    setForm((prev) => {
      const current = prev.visible_in_apps == null ? [...ALL_APP_KEYS] : [...prev.visible_in_apps];
      let next;
      if (checked && !current.includes(key)) {
        next = [...current, key];
      } else if (!checked) {
        next = current.filter((k) => k !== key);
      } else {
        next = current;
      }
      return { ...prev, visible_in_apps: next };
    });
  };

  const isAppChecked = (key) =>
    form.visible_in_apps == null || form.visible_in_apps.includes(key);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingCode(null);
    setShowForm(false);
    setCustomSuite('');
    setUseCustomSuite(false);
    setSacWeights({});
    setShowWeights(false);
    setUseCustomBlender(false);
    setCopyFromCode('');
  };

  // Prefill the "Add" form from an existing portfolio — everything except the
  // identity fields (portfolio_code and display name), which must stay unique
  // and are left for the user to fill in.
  const handleCopyFrom = (code) => {
    setCopyFromCode(code);
    if (!code) return;
    const src = portfolios.find((p) => p.portfolio_code === code);
    if (!src) return;
    setForm({
      portfolio_code: '',          // identity — left blank
      default_display_name: '',    // identity — left blank
      blender_code: '',            // master SAA — not copied
      simulation_name: '',         // not copied
      suite: '',                   // not copied
      growth_exposure: src.growth_exposure != null ? src.growth_exposure : '',
      growth_range: src.growth_range || '',
      income_yield: null,          // calculated from this portfolio's own SAA after save
      income_yield_error: null,
      return_objective: src.return_objective != null ? src.return_objective : '',
      objective_type: src.objective_type || 'cpi',
      time_horizon: src.time_horizon != null ? src.time_horizon : '',
      display_order: 0,            // not copied
      is_active: src.is_active,
      tailored: !!src.tailored,
      show_performance: src.show_performance !== false,
      visible_in_apps: Array.isArray(src.visible_in_apps) ? src.visible_in_apps : null,
      reporting_frequency: src.reporting_frequency || 'quarterly',
      srm_override: src.srm_override != null ? src.srm_override : '',
      benchmark_override: src.benchmark_override || '',
      inception_date: '',          // not copied
      performance_source: src.performance_source || '',
    });
    // Suite and master SAA are not copied — reset their custom-entry toggles.
    setUseCustomSuite(false);
    setCustomSuite('');
    setUseCustomBlender(false);
  };

  const handleEdit = (portfolio) => {
    setForm({
      portfolio_code: portfolio.portfolio_code,
      blender_code: portfolio.blender_code || '',
      simulation_name: portfolio.simulation_name || '',
      default_display_name: portfolio.default_display_name,
      suite: portfolio.suite || '',
      growth_exposure: portfolio.growth_exposure != null ? portfolio.growth_exposure : '',
      growth_range: portfolio.growth_range || '',
      income_yield: portfolio.income_yield,              // display only (bottom-up)
      income_yield_error: portfolio.income_yield_error,
      return_objective: portfolio.return_objective != null ? portfolio.return_objective : '',
      objective_type: portfolio.objective_type || 'cpi',
      time_horizon: portfolio.time_horizon != null ? portfolio.time_horizon : '',
      display_order: portfolio.display_order || 0,
      is_active: portfolio.is_active,
      tailored: !!portfolio.tailored,
      show_performance: portfolio.show_performance !== false,
      visible_in_apps: Array.isArray(portfolio.visible_in_apps) ? portfolio.visible_in_apps : null,
      reporting_frequency: portfolio.reporting_frequency || 'quarterly',
      srm_override: portfolio.srm_override != null ? portfolio.srm_override : '',
      benchmark_override: portfolio.benchmark_override || '',
      inception_date: portfolio.inception_date || '',
      performance_source: portfolio.performance_source || '',
    });
    setEditingCode(portfolio.portfolio_code);
    setShowForm(true);

    // Check if suite is a custom value
    if (portfolio.suite && !SUITE_OPTIONS.includes(portfolio.suite)) {
      setUseCustomSuite(true);
      setCustomSuite(portfolio.suite);
    } else {
      setUseCustomSuite(false);
      setCustomSuite('');
    }

    // The blender_code is the master SAA pointer. If it isn't a built master SAA
    // yet, fall back to the free-text entry so it can still be assigned.
    const bc = portfolio.blender_code || '';
    setUseCustomBlender(!!bc && !masterSAAs[bc]);
    // The inherited-SAA preview is populated by the form.blender_code effect.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const effectiveSuite = useCustomSuite ? customSuite : form.suite;

    // Expand null → full list so the backend always stores an explicit array.
    // This prevents the migration from re-processing the portfolio on restart.
    const effectiveApps = form.visible_in_apps == null ? [...ALL_APP_KEYS] : form.visible_in_apps;

    const payload = {
      ...form,
      suite: effectiveSuite || null,
      blender_code: form.blender_code || null,
      simulation_name: form.simulation_name || null,
      growth_exposure: form.growth_exposure !== '' ? parseFloat(form.growth_exposure) : null,
      growth_range: form.growth_range.trim() || null,
      income_yield: undefined,        // calculated server-side, not an input
      income_yield_error: undefined,
      return_objective: form.return_objective !== '' ? parseFloat(form.return_objective) : null,
      objective_type: form.objective_type || 'cpi',
      time_horizon: form.time_horizon !== '' ? parseInt(form.time_horizon) : null,
      display_order: parseInt(form.display_order) || 0,
      reporting_frequency: form.reporting_frequency || 'quarterly',
      srm_override: form.srm_override !== '' ? parseInt(form.srm_override) : null,
      benchmark_override: form.benchmark_override || null,
      inception_date: form.inception_date || null,
      performance_source: form.performance_source.trim() || null,
      visible_in_apps: effectiveApps,
    };

    try {
      if (editingCode) {
        const updateData = { ...payload };
        // Only include portfolio_code if it changed
        if (updateData.portfolio_code === editingCode) {
          delete updateData.portfolio_code;
        }
        await adminAPI.updateDefaultPortfolio(editingCode, updateData);
        setSuccess(`Portfolio ${updateData.portfolio_code || editingCode} updated successfully`);
        // Keep the edit form open after saving. If the code changed, retarget
        // editingCode to the new value so subsequent saves hit the right record.
        setEditingCode(payload.portfolio_code || editingCode);
        fetchPortfolios();
      } else {
        await adminAPI.createDefaultPortfolio(payload);
        setSuccess(`Portfolio ${payload.portfolio_code} created successfully`);
        resetForm();
        fetchPortfolios();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save portfolio');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete portfolio "${code}"? This will also remove domain overrides and exclusions for this code.`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await adminAPI.deleteDefaultPortfolio(code);
      setSuccess(`Portfolio ${code} deleted`);
      fetchPortfolios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete portfolio');
    }
  };

  const handleToggleActive = async (portfolio) => {
    try {
      await adminAPI.updateDefaultPortfolio(portfolio.portfolio_code, {
        is_active: !portfolio.is_active,
      });
      fetchPortfolios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to toggle active status');
    }
  };

  const handleRenumber = async () => {
    setError(null);
    setSuccess(null);
    try {
      await adminAPI.renumberDefaultPortfolios();
      setSuccess('Display order tidied into a clean sequence');
      fetchPortfolios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to renumber display order');
    }
  };

  // ---- SAC Weights helpers ----
  // Overlay SACs (e.g. CURRENCY) are notional FX-style positions added on top of
  // a 100% physical allocation. Their weight is additive and excluded from the
  // sum-to-1 validation. Identified by `is_overlay` in the taxonomy.
  const overlaySet = React.useMemo(() => {
    if (!taxonomy) return new Set();
    return new Set(taxonomy.sub_asset_classes.filter((s) => s.is_overlay).map((s) => s.sub_asset_class));
  }, [taxonomy]);

  const { physicalTotal, overlayTotal } = React.useMemo(() => {
    let physical = 0;
    let overlay = 0;
    for (const [sac, w] of Object.entries(sacWeights)) {
      if (overlaySet.has(sac)) overlay += w || 0;
      else physical += w || 0;
    }
    return { physicalTotal: physical, overlayTotal: overlay };
  }, [sacWeights, overlaySet]);

  const physicalTotalPct = (physicalTotal * 100).toFixed(1);
  const overlayTotalPct = (overlayTotal * 100).toFixed(1);
  const weightsOk = Object.keys(sacWeights).length === 0 || Math.abs(physicalTotal - 1.0) <= 0.02;

  // Group SAC codes by broad asset class from taxonomy
  const sacGroups = React.useMemo(() => {
    if (!taxonomy) return [];
    const broadOrder = taxonomy.broad_asset_classes.map((b) => b.name);
    const groups = {};
    for (const sac of taxonomy.sub_asset_classes) {
      const broad = sac.broad_asset_class;
      if (!groups[broad]) groups[broad] = [];
      groups[broad].push(sac.sub_asset_class);
    }
    return broadOrder.filter((b) => groups[b]).map((b) => ({ broad: b, sacs: groups[b] }));
  }, [taxonomy]);

  // Build a case/whitespace-insensitive map from normalised key → actual key in sacWeights.
  // This allows the taxonomy SAC strings to match stored DB keys even if they differ
  // by casing, spacing, or underscores (e.g. legacy imports).
  const normSac = (s) => (s || '').toString().trim().toLowerCase().replace(/[\s_-]+/g, '');
  const weightLookup = React.useMemo(() => {
    const m = {};
    for (const k of Object.keys(sacWeights)) m[normSac(k)] = k;
    return m;
  }, [sacWeights]);

  const getWeight = (sac) => {
    const actualKey = weightLookup[normSac(sac)];
    return actualKey != null ? sacWeights[actualKey] : undefined;
  };

  // SAC codes that exist in the stored weights but not in the current taxonomy.
  // We surface these so the user can see them rather than silently dropping data.
  const orphanWeights = React.useMemo(() => {
    if (!taxonomy) return [];
    const taxonomyNorm = new Set(taxonomy.sub_asset_classes.map((s) => normSac(s.sub_asset_class)));
    return Object.keys(sacWeights).filter((k) => !taxonomyNorm.has(normSac(k)));
  }, [taxonomy, sacWeights]);

  // Master SAAs available to assign (built in the Historic SAA tab), sorted.
  const masterSAAOptions = React.useMemo(
    () => Object.keys(masterSAAs).sort(),
    [masterSAAs],
  );
  const selectedMaster = masterSAAs[form.blender_code] || null;

  // Suite dropdown = the three defaults plus every distinct suite already used
  // across existing portfolios, deduped (case-sensitive) and sorted.
  const suiteOptions = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of [...SUITE_OPTIONS, ...portfolios.map((p) => p.suite)]) {
      const v = (s || '').trim();
      if (v && !seen.has(v)) { seen.add(v); out.push(v); }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [portfolios]);

  // Assign a colour to every distinct suite by its position in the sorted list,
  // so each one is visually unique (palette wraps only past ~21 non-brand suites).
  // Brand suites always keep their fixed colour and don't consume a palette slot.
  const suiteColourMap = React.useMemo(() => {
    const map = {};
    let i = 0;
    for (const s of suiteOptions) {
      if (SUITE_COLOURS[s]) {
        map[s] = SUITE_COLOURS[s];
      } else {
        map[s] = SUITE_PALETTE[i % SUITE_PALETTE.length];
        i += 1;
      }
    }
    return map;
  }, [suiteOptions]);
  const suiteColour = (suite) => (suite ? suiteColourMap[suite] || '#6c757d' : '#6c757d');

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Step the edit form through the registry in display order (wraps around).
  // From no selection, Next opens the first portfolio and Prev the last.
  const stepEdit = (delta) => {
    if (!portfolios.length) return;
    const cur = portfolios.findIndex((p) => p.portfolio_code === editingCode);
    const start = cur === -1 ? (delta > 0 ? -1 : portfolios.length) : cur;
    const next = (start + delta + portfolios.length) % portfolios.length;
    handleEdit(portfolios[next]);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="bi bi-collection me-2"></i>Portfolio Registry
        </h4>
        <div className="d-flex gap-2">
          <div className="btn-group" role="group" aria-label="Step through portfolios">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => stepEdit(-1)}
              disabled={!portfolios.length}
              title="Edit the previous portfolio"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled
              style={{ minWidth: 74, pointerEvents: 'none' }}
            >
              {editingCode
                ? `${portfolios.findIndex((p) => p.portfolio_code === editingCode) + 1} / ${portfolios.length}`
                : `${portfolios.length} total`}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => stepEdit(1)}
              disabled={!portfolios.length}
              title="Edit the next portfolio"
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <button
            className="btn btn-outline-secondary"
            onClick={handleRenumber}
            title="Resequence the display order into a clean 1, 2, 3… with no duplicates or gaps"
          >
            <i className="bi bi-sort-numeric-down me-1"></i> Tidy Order
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <i className="bi bi-plus-circle me-1"></i> Add Portfolio
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header" style={{ backgroundColor: '#072040', color: '#fff' }}>
            <h6 className="mb-0">
              {editingCode ? `Edit Portfolio: ${editingCode}` : 'Add New Portfolio'}
            </h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {!editingCode && portfolios.length > 0 && (
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      <i className="bi bi-files me-1"></i>Copy From
                      <span className="text-muted" style={{ fontWeight: 400 }}> (optional)</span>
                    </label>
                    <select
                      className="form-select"
                      value={copyFromCode}
                      onChange={(e) => handleCopyFrom(e.target.value)}
                    >
                      <option value="">-- Start blank --</option>
                      {portfolios.map((p) => (
                        <option key={p.portfolio_code} value={p.portfolio_code}>
                          {p.portfolio_code} — {p.default_display_name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text" style={{ fontSize: '0.72rem' }}>
                      Prefills fields from the selected portfolio. Excludes Portfolio Code, Display Name, Simulation Name, Master SAA, Suite, Inception Date and Display Order.
                    </div>
                  </div>
                </div>
              )}
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Portfolio Code *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.portfolio_code}
                    onChange={(e) => handlePortfolioCodeChange(e.target.value)}
                    required
                    placeholder="e.g. DRU007SU"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Master SAA <span className="text-muted" style={{ fontWeight: 400 }}>(blender code)</span>
                  </label>
                  {useCustomBlender ? (
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={form.blender_code}
                        onChange={(e) => handleChange('blender_code', e.target.value)}
                        placeholder="e.g. DS70"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setUseCustomBlender(false)}
                        title="Choose a built master SAA"
                      >
                        <i className="bi bi-list"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={form.blender_code}
                        onChange={(e) => handleChange('blender_code', e.target.value)}
                      >
                        <option value="">-- None --</option>
                        {masterSAAOptions.map((bc) => (
                          <option key={bc} value={bc}>
                            {bc}{masterSAAs[bc]?.name && masterSAAs[bc].name !== bc ? ` — ${masterSAAs[bc].name}` : ''}
                          </option>
                        ))}
                        {form.blender_code && !masterSAAs[form.blender_code] && (
                          <option value={form.blender_code}>{form.blender_code} (no SAA built)</option>
                        )}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setUseCustomBlender(true)}
                        title="Enter a new blender code"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                    </div>
                  )}
                  <div className="form-text" style={{ fontSize: '0.72rem' }}>
                    SAA weights are managed in the <strong>Historic SAA</strong> tab.
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Display Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.default_display_name}
                    onChange={(e) => handleChange('default_display_name', e.target.value)}
                    required
                    placeholder="e.g. DS 70 Portfolio"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Simulation Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.simulation_name}
                    onChange={(e) => handleChange('simulation_name', e.target.value)}
                    placeholder="e.g. DSP 70 SAA Regression"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Suite</label>
                  {useCustomSuite ? (
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={customSuite}
                        onChange={(e) => setCustomSuite(e.target.value)}
                        placeholder="Custom suite name"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => { setUseCustomSuite(false); setCustomSuite(''); }}
                        title="Use dropdown"
                      >
                        <i className="bi bi-list"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={form.suite}
                        onChange={(e) => handleChange('suite', e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {suiteOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setUseCustomSuite(true)}
                        title="Enter custom suite"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Growth Exposure</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.growth_exposure}
                    onChange={(e) => handleChange('growth_exposure', e.target.value)}
                    min="0"
                    max="1"
                    step="0.01"
                    placeholder="0.0 - 1.0"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Growth Range</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.growth_range}
                    onChange={(e) => handleChange('growth_range', e.target.value)}
                    placeholder="e.g. 60-80%"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label"
                    title="Calculated bottom-up: this portfolio's SAA sub-asset-class weights x the income yields on the CMA Sub Asset Classes tab ('Income = Return' classes use the default scenario's expected return). Not editable here; change the SAA or the sub-asset-class yields.">
                    Income Yield <span className="text-muted" style={{ fontWeight: 400 }}>(calculated)</span>
                  </label>
                  <div className="form-control" style={{ background: 'var(--light-color, #F2F3F2)' }}
                    title={form.income_yield_error || ''}>
                    {form.income_yield != null
                      ? `${(form.income_yield * 100).toFixed(1)}%`
                      : form.income_yield_error
                        ? <span style={{ color: 'var(--danger-color, #F26B43)' }}>Not calculable</span>
                        : <span className="text-muted">After save, from SAA</span>}
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Objective Type</label>
                  <select
                    className="form-select"
                    value={form.objective_type}
                    onChange={(e) => handleChange('objective_type', e.target.value)}
                  >
                    <option value="cpi">CPI+</option>
                    <option value="cash">Cash+</option>
                    <option value="absolute">Absolute</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">
                    {form.objective_type === 'absolute' ? 'Return Obj.' : `Return Obj. (${form.objective_type === 'cash' ? 'Cash+' : 'CPI+'})`}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.return_objective}
                    onChange={(e) => handleChange('return_objective', e.target.value)}
                    min="0"
                    max="0.2"
                    step="0.0025"
                    placeholder="e.g. 0.045"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Time Horizon (yrs)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.time_horizon}
                    onChange={(e) => handleChange('time_horizon', e.target.value)}
                    min="1"
                    max="30"
                    step="1"
                    placeholder="e.g. 7"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.display_order}
                    onChange={(e) => handleChange('display_order', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Report Frequency</label>
                  <select
                    className="form-select"
                    value={form.reporting_frequency}
                    onChange={(e) => handleChange('reporting_frequency', e.target.value)}
                  >
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">SRM Override</label>
                  <select
                    className="form-select"
                    value={form.srm_override}
                    onChange={(e) => handleChange('srm_override', e.target.value)}
                  >
                    <option value="">— Use Calculated —</option>
                    {[1,2,3,4,5,6,7].map((b) => (
                      <option key={b} value={b}>{b} — {SRM_BAND_LABELS[b]}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Benchmark Override</label>
                  <select
                    className="form-select"
                    value={form.benchmark_override}
                    onChange={(e) => handleChange('benchmark_override', e.target.value)}
                  >
                    <option value="">— Default (AMI) —</option>
                    {benchmarkOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Inception Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.inception_date}
                    onChange={(e) => handleChange('inception_date', e.target.value)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Performance Source Disclaimer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.performance_source}
                    onChange={(e) => handleChange('performance_source', e.target.value)}
                    placeholder="e.g. They are calculated from the model portfolio within BT Panorama and as such may vary over time and vary by individual client."
                  />
                  <small className="text-muted">Shown beneath the dashboard performance table. Shared across the suite (first non-empty value is used).</small>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={form.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isActive">Active</label>
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check" title="When ticked, this portfolio is hidden from branded clients unless it is assigned to a domain under Admin → Domains → Portfolio Access.">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="tailored"
                      checked={form.tailored}
                      onChange={(e) => handleChange('tailored', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="tailored">
                      Tailored
                      <span className="text-muted small d-block">Hidden from branded clients unless assigned to a domain</span>
                    </label>
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check" title="When unticked, this portfolio's historic performance is hidden in factsheets, portfolio reports, the dashboard and the viewer.">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="showPerformance"
                      checked={form.show_performance}
                      onChange={(e) => handleChange('show_performance', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showPerformance">
                      Show performance
                      <span className="text-muted small d-block">Untick to hide historic performance in client apps</span>
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold mb-1">Available in Apps</label>
                  <div className="text-muted small mb-2">
                    {form.visible_in_apps == null
                      ? 'Visible in all apps. Untick an app to scope this portfolio.'
                      : `Visible only in: ${form.visible_in_apps.length === 0 ? 'none (hidden everywhere)' : form.visible_in_apps.join(', ')}`}
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    {APP_OPTIONS.map((app) => (
                      <div className="form-check" key={app.key}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`visapp-${app.key}`}
                          checked={isAppChecked(app.key)}
                          onChange={(e) => toggleAppVisibility(app.key, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`visapp-${app.key}`}>
                          {app.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Inherited SAA (read-only) — only when editing an existing portfolio.
                  The SAA is owned by the Historic SAA tab; this shows the current
                  (latest-FY) allocation inherited from the selected master SAA. */}
              {editingCode && taxonomy && (
                <div className="mt-4">
                  {/* Header bar */}
                  <div
                    style={{
                      background: '#072040', color: '#fff', padding: '8px 14px',
                      borderRadius: showWeights ? '6px 6px 0 0' : 6, cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onClick={() => setShowWeights((v) => !v)}
                  >
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      <i className={`bi ${showWeights ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                      Inherited SAA
                      {selectedMaster && (
                        <span style={{ fontWeight: 400, opacity: 0.85, marginLeft: 8 }}>
                          {form.blender_code} · FY{selectedMaster.latest_fy}
                        </span>
                      )}
                    </span>
                    <span style={{
                      fontSize: '0.78rem', fontWeight: 600,
                      color: !selectedMaster ? '#adb5bd' : weightsOk ? '#91CF9B' : '#ffc107',
                    }}>
                      {!selectedMaster
                        ? 'No master SAA'
                        : overlayTotal > 0
                          ? `${physicalTotalPct}% + ${overlayTotalPct}% overlay`
                          : `${physicalTotalPct}%`}
                    </span>
                  </div>

                  {showWeights && (
                    <div style={{ border: '1px solid #072040', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: 14 }}>
                      {!selectedMaster ? (
                        <div className="text-muted small">
                          {form.blender_code
                            ? <>No SAA has been built for <strong>{form.blender_code}</strong> yet. Create it in the <strong>Historic SAA</strong> tab.</>
                            : <>Select a <strong>Master SAA</strong> above to inherit its strategic allocation.</>}
                        </div>
                      ) : (
                        <>
                          <div className="text-muted small mb-2">
                            Read-only. Inherited from master SAA <strong>{form.blender_code}</strong> (FY{selectedMaster.latest_fy}).
                            Edit weights in the <strong>Historic SAA</strong> tab.
                          </div>

                          {/* Weights table grouped by broad asset class */}
                          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                              <thead>
                                <tr>
                                  <th style={{ padding: '4px 8px', borderBottom: '2px solid #072040', width: '60%' }}>Sub Asset Class</th>
                                  <th style={{ padding: '4px 8px', borderBottom: '2px solid #072040', width: '40%', textAlign: 'right' }}>Weight (%)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sacGroups.map((g) => {
                                  const visible = g.sacs.filter((sac) => getWeight(sac) != null);
                                  if (visible.length === 0) return null;
                                  return (
                                    <React.Fragment key={g.broad}>
                                      <tr>
                                        <td colSpan={2} style={{
                                          padding: '6px 8px 3px', fontWeight: 700, fontSize: '0.73rem',
                                          color: '#072040', borderBottom: '1px solid #e0e0e0', background: '#f5f6f8',
                                        }}>
                                          {g.broad}
                                        </td>
                                      </tr>
                                      {visible.map((sac) => {
                                        const w = getWeight(sac);
                                        const isOverlay = overlaySet.has(sac);
                                        return (
                                          <tr key={sac}>
                                            <td style={{ padding: '3px 8px 3px 20px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace' }}>
                                              {sac}
                                              {isOverlay && (
                                                <span
                                                  title="Overlay SAC — additive on top of the 100% physical allocation"
                                                  style={{
                                                    marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                                                    background: '#f0a500', color: '#072040',
                                                    fontFamily: 'inherit', fontSize: '0.65rem', fontWeight: 700,
                                                  }}
                                                >
                                                  overlay
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontFamily: 'monospace' }}>
                                              {(w * 100).toFixed(2)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                                {orphanWeights.length > 0 && (
                                  <React.Fragment>
                                    <tr>
                                      <td colSpan={2} style={{
                                        padding: '6px 8px 3px', fontWeight: 700, fontSize: '0.73rem',
                                        color: '#dc3545', borderBottom: '1px solid #e0e0e0', background: '#fff3cd',
                                      }}>
                                        Unmatched codes (not in current taxonomy)
                                      </td>
                                    </tr>
                                    {orphanWeights.map((sac) => (
                                      <tr key={`orphan-${sac}`}>
                                        <td style={{ padding: '3px 8px 3px 20px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace', color: '#dc3545' }}>{sac}</td>
                                        <td style={{ padding: '3px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontFamily: 'monospace', color: '#dc3545' }}>
                                          {((sacWeights[sac] || 0) * 100).toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                )}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td style={{ padding: '6px 8px', fontWeight: 700, borderTop: '2px solid #072040' }}>
                                    Physical Total{overlayTotal > 0 ? ' (excl. overlay)' : ''}
                                  </td>
                                  <td style={{
                                    padding: '6px 8px', fontWeight: 700, textAlign: 'right', borderTop: '2px solid #072040',
                                    color: weightsOk ? '#198754' : '#dc3545',
                                  }}>
                                    {physicalTotalPct}%
                                  </td>
                                </tr>
                                {overlayTotal > 0 && (
                                  <tr>
                                    <td style={{ padding: '4px 8px', fontWeight: 600, color: '#6c757d', fontStyle: 'italic' }}>
                                      Overlay (additive)
                                    </td>
                                    <td style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right', color: '#6c757d' }}>
                                      +{overlayTotalPct}%
                                    </td>
                                  </tr>
                                )}
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2">
                  <i className={`bi ${editingCode ? 'bi-check-lg' : 'bi-plus-circle'} me-1`}></i>
                  {editingCode ? 'Save Changes' : 'Create Portfolio'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: '0.75rem',
        }}>
          <thead>
            <tr>
              {['#', 'Code', 'Blender', 'Display Name', 'Suite', 'Growth', 'Yield', 'Objective', 'Horizon', 'SRM', 'Benchmark', 'Freq', 'Inception', 'Active', ''].map((h, i) => (
                <th key={i} style={{
                  background: '#072040',
                  color: '#fff',
                  fontWeight: 600,
                  padding: '6px 8px',
                  whiteSpace: 'nowrap',
                  textAlign: i === 14 ? 'right' : 'left',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  borderBottom: '2px solid #002244',
                  fontSize: '0.72rem',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {portfolios.map((p) => {
              const rowStyle = {
                padding: '4px 8px',
                whiteSpace: 'nowrap',
                borderBottom: '1px solid #f0f0f0',
                verticalAlign: 'middle',
                opacity: p.is_active ? 1 : 0.45,
              };
              return (
                <tr key={p.portfolio_code} style={{ cursor: 'pointer' }} onClick={() => handleEdit(p)}>
                  <td style={rowStyle}>{p.display_order}</td>
                  <td style={{ ...rowStyle, fontFamily: 'monospace', color: '#599BE3', fontWeight: 600 }}>
                    {p.portfolio_code}
                    {p.has_weights && <i className="bi bi-pie-chart-fill ms-1" style={{ color: '#f0a500', fontSize: '0.7rem' }} title="Has SAC weights"></i>}
                  </td>
                  <td style={{ ...rowStyle, fontFamily: 'monospace', color: '#6c757d' }}>{p.blender_code || '-'}</td>
                  <td style={{ ...rowStyle, whiteSpace: 'normal', minWidth: 140, maxWidth: 200, fontWeight: 500, color: '#072040' }}>{p.default_display_name}</td>
                  <td style={rowStyle}>
                    {p.suite ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 3,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        backgroundColor: suiteColour(p.suite),
                        color: '#fff',
                      }}>
                        {p.suite}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ ...rowStyle, textAlign: 'center' }}>{p.growth_exposure != null ? p.growth_exposure.toFixed(2) : '-'}</td>
                  <td style={{ ...rowStyle, textAlign: 'center' }} title={p.income_yield_error || 'Bottom-up: SAA x sub-asset-class income yields'}>
                    {p.income_yield != null
                      ? (p.income_yield * 100).toFixed(1) + '%'
                      : p.income_yield_error
                        ? <span style={{ color: 'var(--danger-color, #F26B43)' }}>n/a</span>
                        : '-'}
                  </td>
                  <td style={rowStyle}>{p.return_objective != null
                    ? (p.objective_type || 'cpi') === 'absolute'
                      ? (p.return_objective * 100).toFixed(2) + '%'
                      : ((p.objective_type || 'cpi') === 'cash' ? 'Cash+' : 'CPI+') + (p.return_objective * 100).toFixed(2) + '%'
                    : '-'}</td>
                  <td style={{ ...rowStyle, textAlign: 'center' }}>{p.time_horizon != null ? p.time_horizon + 'yr+' : '-'}</td>
                  <td style={rowStyle}>
                    {p.srm_override != null
                      ? <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 3, fontSize: '0.65rem', fontWeight: 600, backgroundColor: '#f0a500', color: '#072040' }}>{p.srm_override} — {SRM_BAND_LABELS[p.srm_override]}</span>
                      : <span style={{ color: '#adb5bd' }}>—</span>}
                  </td>
                  <td style={rowStyle}>
                    {p.benchmark_override
                      ? <span style={{ fontWeight: 600, color: '#599BE3' }}>{p.benchmark_override}</span>
                      : <span style={{ color: '#adb5bd' }}>AMI</span>}
                  </td>
                  <td style={{ ...rowStyle, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: 3,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      backgroundColor: p.reporting_frequency === 'both' ? '#599BE3' : p.reporting_frequency === 'monthly' ? '#f0a500' : '#6c757d',
                      color: '#fff',
                    }}>
                      {(p.reporting_frequency || 'quarterly').charAt(0).toUpperCase()}
                    </span>
                  </td>
                  <td style={rowStyle}>{p.inception_date || '-'}</td>
                  <td style={{ ...rowStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <div className="form-check form-switch" style={{ minHeight: 'auto', marginBottom: 0, display: 'flex', justifyContent: 'center' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={p.is_active}
                        onChange={() => handleToggleActive(p)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </td>
                  <td style={{ ...rowStyle, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-sm p-0 me-2"
                      onClick={() => handleEdit(p)}
                      title="Edit"
                      style={{ color: '#599BE3', border: 'none', background: 'none', fontSize: '0.85rem' }}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </button>
                    <button
                      className="btn btn-sm p-0"
                      onClick={() => handleDelete(p.portfolio_code)}
                      title="Delete"
                      style={{ color: '#dc3545', border: 'none', background: 'none', fontSize: '0.85rem' }}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
            {portfolios.length === 0 && (
              <tr>
                <td colSpan="15" style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  No portfolios configured. Click "Add Portfolio" to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioRegistryEditor;
